import type { Session } from '@supabase/supabase-js'
import { createContext, useContext, useEffect, useState } from 'react'
import { adoptSessionFromUrl } from './adopt-session'
import { maybeRedirectToHub } from './sso-hand-off'
import { supabase } from './supabase'

interface AuthState {
  session: Session | null
  loading: boolean
  signOut: () => Promise<void>
}

const AuthCtx = createContext<AuthState>({ session: null, loading: true, signOut: async () => {} })

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    let active = true
    // Adopt a cross-domain hand-off session (from the Navo24 host) BEFORE reading the session, so an
    // owner arriving via "Open Schedules" lands authenticated instead of flashing /login. No-op when
    // there's no hand-off token in the URL.
    ;(async () => {
      const hadNonce = window.location.hash.includes('sso=')
      await adoptSessionFromUrl()
      const { data } = await supabase.auth.getSession()
      if (!active) return
      // First signed-out load of this tab: one trip to the hub so a hub session carries over. Sets its
      // own marker first; never loops. Leaves the session, storageKey and adapter untouched.
      if (maybeRedirectToHub(Boolean(data.session), hadNonce)) return
      setSession(data.session)
      setLoading(false)
    })()
    const { data: sub } = supabase.auth.onAuthStateChange((_e, s) => setSession(s))
    return () => {
      active = false
      sub.subscription.unsubscribe()
    }
  }, [])

  return (
    <AuthCtx.Provider
      value={{ session, loading, signOut: async () => void (await supabase.auth.signOut()) }}
    >
      {children}
    </AuthCtx.Provider>
  )
}

export const useAuth = () => useContext(AuthCtx)
